import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardHeader } from "../../components/ui/Card";
import { ErrorDetail } from "../../components/ui/ErrorDetail";
import { ErrorState } from "../../components/ui/ErrorState";
import { Loading } from "../../components/ui/Page";
import { Table, Td, Th } from "../../components/ui/Table";
import { useResource } from "../../hooks/useResource";
import { streamsApi } from "../../lib/api/streams";
import type { Platform } from "@sp/shared/enums";
import type { StreamEvent } from "../../lib/api/types";
import { formatBytes, formatDateTime, formatSpan } from "../../lib/format";
import { errorDetailText, exitReasonText } from "../../lib/stream-status";
import { UptimeTimeline } from "./UptimeTimeline";

/** Recent runs: one row per time the stream process ran. `version` changes when the stream's state does. */
export function RunsCard({ streamId, platform, version }: { streamId: string; platform: Platform; version: string }) {
  const runs = useResource((signal) => streamsApi.runs(streamId, 10, signal), [streamId, version]);
  return (
    <Card>
      <CardHeader title="Runs" caption="each time the stream process started, the last 10" />
      {runs.error !== undefined && <div className="p-4"><ErrorState error={runs.error} onRetry={() => void runs.reload()} /></div>}
      {!runs.data && runs.loading && <div className="px-5"><Loading /></div>}
      {runs.data && runs.data.length === 0 && <p className="px-5 py-4 text-muted">// no runs yet. start the stream to see them here.</p>}
      {runs.data && runs.data.length > 0 && <UptimeTimeline runs={runs.data} now={Date.now()} />}
      {runs.data && runs.data.length > 0 && (
        <Table label="Runs" minWidth={640}>
          <thead><tr><Th>started</Th><Th>live for</Th><Th>sent</Th><Th>ended because</Th></tr></thead>
          <tbody>
            {runs.data.map((r) => {
              const end = r.endedAt ? Date.parse(r.endedAt) : Date.now();
              const detail = errorDetailText(r.errorSummary);
              return (
                <tr key={r.id}>
                  <Td>{formatDateTime(r.startedAt)}</Td>
                  <Td>{r.wentLiveAt ? formatSpan(end - Date.parse(r.wentLiveAt)) : "never went live"}</Td>
                  <Td>{formatBytes(r.bytesOut)}</Td>
                  <Td className="text-muted">
                    {r.endedAt ? (exitReasonText(r.exitReason, platform) ?? "-") : "still running"}
                    {detail && <div className="mt-1"><ErrorDetail text={detail} /></div>}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

/** Every state change, newest first, with "load older" paging (the API pages by event id). */
export function EventsCard({ streamId, version }: { streamId: string; version: string }) {
  const first = useResource((signal) => streamsApi.events(streamId, { limit: 20 }, signal), [streamId, version]);
  const [older, setOlder] = useState<StreamEvent[]>([]);
  const [cursor, setCursor] = useState<number | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);

  const next = cursor === undefined ? first.data?.nextBefore : cursor;
  const events = [...(first.data?.events ?? []), ...older];
  const loadOlder = async () => {
    if (!next) return;
    setLoadingMore(true);
    try {
      const page = await streamsApi.events(streamId, { limit: 50, before: next });
      setOlder((o) => [...o, ...page.events]);
      setCursor(page.nextBefore);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Events" caption="state changes, newest first" />
      {first.error !== undefined && <div className="p-4"><ErrorState error={first.error} onRetry={() => void first.reload()} /></div>}
      {!first.data && first.loading && <div className="px-5"><Loading /></div>}
      {first.data && (
        <ol className="flex flex-col">
          {events.map((e) => (
            <li key={e.id} className="flex flex-wrap gap-x-4 gap-y-1 border-b border-line px-5 py-2.5">
              <time dateTime={e.createdAt} className="text-muted tabular-nums">{formatDateTime(e.createdAt)}</time>
              <span className="font-mono text-xs font-bold">{e.fromState ?? "·"} → {e.toState}</span>
              <span className="text-muted">{e.actor}{e.reason ? `: ${e.reason}` : ""}</span>
            </li>
          ))}
          {events.length === 0 && <li className="px-5 py-4 text-muted">// nothing has happened yet.</li>}
        </ol>
      )}
      {next ? (
        <div className="p-4"><Button size="sm" pending={loadingMore} onClick={() => void loadOlder()}>load older</Button></div>
      ) : null}
    </Card>
  );
}
